<template>
    <span
        :aria-placeholder="placeholder"
        class="f:16 lh:1.5 flex:1 outline:none {content:attr(aria-placeholder);color:#A9A9A9;z:-1}:empty::after inline-block:empty word-break:break-all cursor:text"
        :contenteditable="editable"
        @focus="emit('focus')"
        @input="sync"
        @paste.prevent="paste"
        ref="input"
    ></span>
</template>

<script setup>
    const input = ref();

    const props = defineProps({
        autofocus: { default: false, type: Boolean },
        comment: { default: false, type: Boolean },
        editable: { default: false, type: Boolean },
        maxLength: { default: 0, type: Number },
        modelValue: { type: String },
        placeholder: { type: String },
        single: { default: false, type: Boolean }
    });

    const emit = defineEmits(["focus", "submit", "update:modelValue"]);

    const focus = () => {
        input.value.focus();
    };

    const getCaretPosition = (element) => {
        const selection = window.getSelection();

        if (selection.rangeCount > 0) {
            const range = selection.getRangeAt(0);
            const preCaretRange = range.cloneRange();

            preCaretRange.selectNodeContents(element);
            preCaretRange.setEnd(range.endContainer, range.endOffset);

            return preCaretRange.toString().length;
        }

        return 0;
    }

    const paste = (event) => {
        let text = "";

        if (event.clipboardData) {
            text = (event.originalEvent || event).clipboardData.getData("text/plain");
        } else if (window.clipboardData) {
            text = window.clipboardData.getData("Text");
        }

        if (props.single) {
            text = text.replace(/[\n\r]/g, "");
        }

        const range = document.getSelection().getRangeAt(0);
        range.deleteContents();

        const textNode = document.createTextNode(text);
        range.insertNode(textNode);
        range.selectNodeContents(textNode);
        range.collapse(false);

        const selection = window.getSelection();
        selection.removeAllRanges();
        selection.addRange(range);

        sync();
    };

    const setCaretPosition = (element, position) => {
        const range = document.createRange();
        const selection = window.getSelection();
        let node = element;
        let charIndex = 0;
        let found = false;

        const traverseNodes = (node) => {
            for (let i = 0; i < node.childNodes.length && !found; i++) {
                const child = node.childNodes[i];

                if (child.nodeType === 3) {
                    const nextCharIndex = charIndex + child.length;

                    if (position >= charIndex && position <= nextCharIndex) {
                        range.setStart(child, position - charIndex);
                        range.setEnd(child, position - charIndex);
                        found = true;
                    }

                    charIndex = nextCharIndex;
                } else {
                    traverseNodes(child);
                }
            }
        };

        traverseNodes(node);

        selection.removeAllRanges();
        selection.addRange(range);
    };

    const sync = () => {
        if (props.maxLength && props.maxLength < input.value.innerText.length) {
            const position = getCaretPosition(input.value);

            input.value.innerText = input.value.innerText.slice(0, props.maxLength);

            setCaretPosition(input.value, Math.min(position, props.maxLength));
        }

        emit("update:modelValue", input.value.innerText);
    };

    defineExpose({ focus });

    onMounted(() => {
        if (props.autofocus && props.editable) {
            setTimeout(focus, 300);
        }
    });

    watch(input, () => {
        if (input.value) {
            input.value.innerText = props.modelValue;

            input.value.addEventListener("keydown", (event) => {
                if (event.key === "Enter" && !event.isComposing) {
                    if (props.single || (props.comment && !event.altKey && !event.ctrlKey && !event.metaKey && !event.shiftKey)) {
                        event.preventDefault();
                        emit("submit");
                    }
                }
            });
        }
    });

    watch(() => props.modelValue, () => {
        if (input.value.innerText !== props.modelValue) {
            input.value.innerText = props.modelValue;
        }
    });
</script>
